import React from 'react';
import Paper from '@material-ui/core/Paper';
import { t } from 'ttag';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import useReactRouter from 'use-react-router';
import useRouter from 'use-react-router';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Switch from '@material-ui/core/Switch';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import { Tooltip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FilterListIcon from '@material-ui/icons/FilterList';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { DatePicker } from '@material-ui/pickers';
import { MTableToolbar } from 'material-table';
import queryString from 'query-string';
import Link from '@material-ui/core/Link';
import Loading from '../../../components/Loading';
import {
  BASE_STATUS,
  BASE_STATUS_TITLES,
  USER_STATUS,
} from '../../../utils/enums';
import useNotifier from '../../../components/Messages/hooks';
import {
  formatPublicDate,
  getCurrentYear,
  insertAt,
  nexYear,
  prepareParam,
} from '../../../utils/helpers';
import useApi, { MyClubCApi } from '../../../api';
import useUser from '../../../auth/hooks';
import DataTable from '../../../components/DataTable';
import MyChip from '../../../components/MyChip';
import AddContributionDialog from '../../../components/ContributionDialog';

const ContributionListDialog = ({
  trainerId,
  user,
  open,
  toggle,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const api = useApi();
  const pushMessage = useNotifier();
  const { loading, data } = useQuery(api.schema.contributions.many()[0], {
    fetchPolicy: 'network-only',
    variables: { inputFilter: { userId: user.id, trainerId } },
  });

  const [doDelete] = useMutation(api.schema.contributions.delete()[0], {
    onCompleted: (d) => {
      const res = d[api.schema.contributions.delete()[1]];
      if (res.ok) {
        pushMessage({ text: 'Успішно видалено' });
      }
    },
  });

  const handleOnDelete = (data) => {
    doDelete({ variables: { id: data.id } });
    onDelete(data);
  };

  return (
    <Dialog scroll='body' open={open} onClose={() => toggle(false)}>
      <>
        <DialogTitle>{`Спортсмен ${user.fullName}`}</DialogTitle>
        <DialogContent>
          <DataTable
            isLoading={loading}
            title='Членскі внески'
            pageSize={5}
            columns={[
              { title: t`Id`, field: 'id', editable: 'never' },
              {
                title: t`Тренер`,
                field: 'trainer',
                editable: 'never',
                render: ({ trainer }) => {
                  return (
                    <Link
                      target='_blank'
                      href={api.router.trainers.edit(trainer.id)}
                    >
                      {trainer.fullName}
                    </Link>
                  );
                },
              },
              {
                title: t`Зал`,
                field: 'gym',
                editable: 'never',
                render: ({ gym }) => {
                  return (
                    <Link target='_blank' href={api.router.gyms.edit(gym.id)}>
                      {gym.name}
                    </Link>
                  );
                },
              },
              { title: t`Рік`, field: 'year' },
              {
                title: t`Дата внеску`,
                field: 'addedAt',
                render: ({ addedAt }) => formatPublicDate(addedAt),
              },
              { title: t`Внесок`, field: 'amount' },
            ]}
            data={data ? data[api.schema.contributions.many()[1]] : []}
            onDelete={handleOnDelete}
            customActions={[
              {
                icon: 'edit',
                tooltip: t`Редагувати внесок`,
                onClick: (event, rowData) => onEdit(rowData),
              },
            ]}
          />
        </DialogContent>
        <DialogActions>
          <Button
            style={{ margin: '20px 0 20px 0' }}
            margin='normal'
            type='button'
            variant='contained'
            color='primary'
            onClick={onAdd}
          >
            {t`Додати внесок`}
          </Button>

          <Button onClick={() => toggle(false)} color='primary' autoFocus>
            {t`Відмінити`}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  );
};

const ListView = () => {
  const { history } = useRouter();
  const { user } = useUser();
  const api = useApi();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);
  const tableRef = React.useRef();
  const { match } = useReactRouter();
  const { trainerId } = match.params;
  const [loading, setLoading] = React.useState(false);
  const [bulkSelect, setBulkSelect] = React.useState(false);
  const [selectedUsers, setSelectedUsers] = React.useState([]);
  const [trainer, setTrainer] = React.useState(null);
  const [contributionUsers, setContributionUsers] = React.useState([]);
  const [gyms, setGyms] = React.useState([]);
  const searchParams = new URLSearchParams(history.location.search);
  const [pageState, setPageState] = React.useState({
    gymId: parseInt(searchParams.get('gymId'), 10) || undefined,
    year: searchParams.get('year') || String(getCurrentYear()),
  });

  const [state, setState] = React.useState({
    isOpen: false,
    isOpenList: false,
    user: null,
    contribution: null,
    gymId: localStorage.getItem('contrib_gym_id'),
    analyticLoad: true,
  });

  const updateHistory = (searchParams) => {
    history.replace({
      search: `?${queryString.stringify(searchParams)}`,
    });
  };

  const fetchContributionUsers = async () => {
    setLoading(true);
    updateHistory(prepareParam(pageState));
    const res = await newApi.getContributionUsers({
      trainerId: trainerId,
      year: pageState.year,
      gymId: pageState.gymId,
    });
    setContributionUsers(res);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchContributionUsers();
  }, [pageState]);

  React.useEffect(() => {
    async function fetchData() {
      const gyms = await newApi.getGyms();
      let trainer = await newApi.getTrainer(trainerId);
      setTrainer(trainer);
      setGyms([{ name: 'Немає залу', id: 0 }, ...gyms]);
    }
    fetchData();
  }, []);

  const columns = [
    {
      title: t`Id`,
      field: 'id',
      editable: 'never',
      filtering: false,
      width: 4,
    },
    {
      title: t`Спортсмен`,
      field: 'fullName',
      filtering: false,
      render: (r) => {
        return (
          <Link target='_blank' href={api.router.athletes.edit(r.id)}>
            {r.fullName}
          </Link>
        );
      },
    },
    {
      title: t`Зал`,
      field: 'gyms',
      render: ({ contributionGym, gyms }) => {
        if (!contributionGym) {
          return gyms.map((r) => (
            <Chip
              key={r.id}
              variant='outlined'
              color='primary'
              size='small'
              label={r.name}
            />
          ));
        }
        const gIds = gyms.map((r) => r.id);
        let diff = [contributionGym].filter((g) => !gIds.includes(g.id));
        return gyms
          .map((r) => (
            <MyChip
              key={r.id}
              variant='outlined'
              color='#2196f3'
              size='small'
              label={r.name}
            />
          ))
          .concat(
            diff.map((r) => (
              <Tooltip key={r.id} title={t`Не тренується в цьму залі`}>
                <span>
                  <MyChip
                    key={r.id}
                    color='grey'
                    variant='outlined'
                    size='small'
                    label={r.name}
                  />
                </span>
              </Tooltip>
            ))
          );
      },
    },
    {
      title: t`Статус`,
      field: 'status',
      filtering: false,
      render: ({ status }) => {
        const colorMap = {
          [BASE_STATUS.blocked]: 'secondary',
          [BASE_STATUS.active]: 'primary',
        };
        return (
          <Chip
            label={BASE_STATUS_TITLES[status]}
            color={colorMap[status] || 'default'}
            variant='outlined'
          />
        );
      },
    },
    {
      title: t`Внески`,
      field: 'amount',
      with: 10,
      filtering: false,
      render: ({ amount, payToAnother }) => (
        <MyChip
          label={amount}
          color={amount !== '0' && !payToAnother ? 'green' : '#333'}
          variant='outlined'
        />
      ),
    },
    {
      title: t`Додаткова інформація`,
      field: 'status',
      filtering: false,
      render: ({
        payToAnother,
        transfer,
        transferToTrainerId,
        payToAnotherTrainerId,
        isAthlete,
      }) => (
        <>
          {!isAthlete && (
            <Chip
              label={<span>{`Не в статусі спортсмена `}</span>}
              color='secondary'
              variant='outlined'
            />
          )}
          {payToAnother && (
            <Chip
              label={
                <span>
                  {`Внесок зроблено іншому `}
                  <Link
                    target='_blank'
                    href={api.router.trainers.edit(payToAnotherTrainerId)}
                    variant='body2'
                  >{t`тренеру`}</Link>
                </span>
              }
              color='secondary'
              variant='outlined'
            />
          )}
          {transfer && (
            <>
              <br />
              <Chip
                label={
                  <span>
                    {`Перейшов до іншого `}
                    <Link
                      target='_blank'
                      href={api.router.trainers.edit(transferToTrainerId)}
                      variant='body2'
                    >{t`тренера`}</Link>
                  </span>
                }
                color='secondary'
                variant='outlined'
              />
            </>
          )}
        </>
      ),
    },
  ];
  const adminColumns = [
    {
      title: t`Клуб`,
      field: 'club',
      filtering: false,
      render: ({ club }) => club.shortName,
    },
  ];
  user.isAdmin && insertAt(columns, 1, ...adminColumns);

  const handleOnCreate = () => {
    if (bulkSelect) {
      setState({
        ...state,
        isOpenList: false,
        isOpen: false,
        contribution: null,
      });
      resetSelectedUsers();
    } else {
      setState({
        ...state,
        isOpenList: true,
        isOpen: false,
        contribution: null,
      });
    }
    fetchContributionUsers();
  };

  const handleOnUpdate = (contribution) => {
    setState({
      ...state,
      isOpen: true,
      isOpenList: false,
      contribution,
    });
    fetchContributionUsers();
  };

  const handleOnDelete = () => {
    fetchContributionUsers();
  };

  const isInSelectedGymNow = (u) => {
    if (pageState.gymId === undefined) return true;
    if (pageState.gymId === 0) return (u.gyms?.length ?? 0) === 0;
    return u.gyms?.some((g) => g.id === pageState.gymId);
  };

  const activeAthletes = contributionUsers.filter(
    (u) =>
      u.status === USER_STATUS.active &&
      !u.transferToTrainerId &&
      isInSelectedGymNow(u)
  );
  const sumAmount = (a, b) => Number(a) + Number(b);

  const NewTitle = () => (
    <h6
      className='MuiTypography-root MuiTypography-h6'
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {trainer
        ? t`${trainer.fullName} (членскі внески за ${pageState.year})`
        : '...'}
      <div style={{ fontWeight: 'normal', fontSize: '15px' }}>
        <span>
          {t`Всьго активних спортсменів: `}
          <b>{activeAthletes.length}</b>
        </span>
        <span>
          {t` Сума внесків: `}{' '}
          <b>
            {contributionUsers
              .filter((u) => !u.payToAnother)
              .map((u) => u.amount)
              .reduce(sumAmount, 0)}
          </b>
        </span>
      </div>
      <div style={{ fontWeight: 'normal', fontSize: '15px' }}></div>
    </h6>
  );

  const resetSelectedUsers = () => {
    if (selectedUsers.length > 0) {
      try {
        tableRef.current.dataManager.changeRowSelected(false, [
          selectedUsers.map((r, index) => index),
        ]);
      } catch (e) {
        // pass
      }
    }
    setSelectedUsers([]);
  };

  const onChangeYear = async (e) => {
    setPageState({ ...pageState, year: String(e.getFullYear()) });
  };

  if (loading) return <Loading />;

  return (
    <Paper>
      <DataTable
        tableRef={tableRef}
        selection={bulkSelect}
        selectionProps={(rowData) => ({
          tooltip:
            rowData.gyms?.length === 0
              ? t`Спортсмен без группи`
              : t`Додати внесок`,
          disabled: rowData.gyms?.length === 0,
        })}
        showTextRowsSelected={bulkSelect}
        isLoading={loading}
        title={<NewTitle />}
        columns={columns}
        data={contributionUsers}
        customActions={[
          !bulkSelect && {
            icon: 'visibility',
            tooltip: t`Список внесків`,
            onClick: (event, rowData) =>
              setState({
                ...state,
                isOpenList: true,
                user: rowData,
                contribution: null,
              }),
          },
          (rowData) => ({
            icon: 'addBox',
            tooltip:
              rowData.gyms?.length === 0
                ? t`Спортсмен без группи`
                : t`Додати внесок`,
            disabled: rowData.gyms?.length === 0,
            onClick: (event, rowData) =>
              setState({
                ...state,
                isOpen: true,
                user: rowData,
                contribution: null,
              }),
          }),
        ]}
        onSelectionChange={(rows) => setSelectedUsers(rows)}
        components={{
          Toolbar: (props) => (
            <div>
              <MTableToolbar {...props} />
              <Grid
                container
                spacing={2}
                style={{ marginLeft: '10px', marginTop: '5px' }}
                justifyContent='flex-start'
                alignItems='center'
              >
                <Grid item>
                  <Tooltip title={t`Виберіть фільтр`}>
                    <FilterListIcon />
                  </Tooltip>
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={gyms}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть зал`}
                        variant='outlined'
                      />
                    )}
                    value={gyms.find((g) => g && g.id === pageState.gymId)}
                    onChange={(e, value) =>
                      setPageState({ ...pageState, gymId: value && value.id })
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <DatePicker
                    size={'small'}
                    variant='inline'
                    label={t`Рік внеску`}
                    InputAdornmentProps={{ position: 'start' }}
                    inputVariant='outlined'
                    openTo='year'
                    format='yyyy'
                    views={['year']}
                    value={pageState.year}
                    cancelLabel='Відмінити'
                    maxDate={nexYear()}
                    onChange={onChangeYear}
                  />
                </Grid>
              </Grid>
              <div style={{ padding: '0px 10px' }}>
                <FormControlLabel
                  value='start'
                  control={
                    <Switch
                      checked={bulkSelect}
                      onChange={(e, checked) => {
                        setBulkSelect(checked);
                        if (checked) {
                          setContributionUsers(
                            contributionUsers.filter((u) => u.gyms?.length >= 1)
                          );
                        } else {
                          fetchContributionUsers();
                        }
                        resetSelectedUsers();
                      }}
                      color='primary'
                    />
                  }
                  label={t`Активувати масові внески:`}
                  labelPlacement='start'
                />
              </div>
            </div>
          ),
        }}
      />
      {state.isOpen && (
        <AddContributionDialog
          trainerId={trainerId}
          user={state.user}
          open={state.isOpen}
          contribution={state.contribution}
          userIds={selectedUsers.map((r) => r.id)}
          toggle={(v) => setState({ ...state, isOpen: v })}
          onSubmit={handleOnCreate}
        />
      )}
      {state.isOpenList && (
        <ContributionListDialog
          trainerId={trainerId}
          open={state.isOpenList}
          user={state.user}
          toggle={(v) => setState({ isOpenList: v })}
          onAdd={() => setState({ ...state, isOpen: true, isOpenList: false })}
          onEdit={handleOnUpdate}
          onDelete={handleOnDelete}
        />
      )}
    </Paper>
  );
};

export default ListView;
